import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { useCallback, useMemo } from 'react';

import { PaymentMethods } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getFees } from '@/redux/modules/processingFee';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    amountToPayZip: number;
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency: string;
    isSelected: boolean;
    onSelect: (paymentMethod: PaymentMethods) => void;
    paymentMethod: PaymentMethods;
    requireACHForCheckout: boolean;
};

export const PaymentMethodCard = ({
    amountToPayZip,
    bidderPaysProcessingFee,
    catalogId,
    currency,
    isSelected,
    onSelect,
    paymentMethod,
    requireACHForCheckout,
}: Props) => {
    const formatMessage = useAppFormatMessage();

    const processingFees = useAppSelector((state) => getFees(state, catalogId));

    const processingFeeDifference = processingFees ? processingFees.ccFee - processingFees.achFee : 0;

    const { subtitle, title } = useMemo(() => {
        switch (paymentMethod) {
            case PaymentMethods.CreditCard:
            default:
                return {
                    title: <LabelStrong>{formatMessage(['checkout', 'payment', 'creditCards'])}</LabelStrong>,
                };
            case PaymentMethods.Ach:
                const requiresSubtitle =
                    !requireACHForCheckout && processingFeeDifference > 0 && bidderPaysProcessingFee;
                return {
                    subtitle: requiresSubtitle ? (
                        <BodySecondary color="secondary">
                            {formatMessage(['checkout', 'payment', 'achFees'], {
                                values: {
                                    processingFee: (
                                        <FormattedNumber
                                            currency={currency}
                                            key="checkout-payment-ach-fees-processing-fee"
                                            style="currency"
                                            value={processingFeeDifference / 100}
                                        />
                                    ),
                                },
                            })}
                        </BodySecondary>
                    ) : null,
                    title: <LabelStrong>{formatMessage(['checkout', 'payment', 'ach'])}</LabelStrong>,
                };
            case PaymentMethods.Zip:
                return {
                    subtitle: (
                        <BodySecondary color="secondary">
                            {formatMessage(['checkout', 'payment', 'zipPaymentsPlanShort'], {
                                values: {
                                    amount: (
                                        <FormattedNumber
                                            currency={currency}
                                            key="zip-payment-method-card-amount"
                                            style="currency"
                                            value={amountToPayZip}
                                        />
                                    ),
                                },
                            })}
                        </BodySecondary>
                    ),
                    title: (
                        <img
                            alt="ZIP Logo"
                            className="mx-2xs my-0 w-[40px]"
                            src="https://p1.liveauctioneers.com/dist/images/zip-logo.svg"
                        />
                    ),
                };
        }
    }, [
        amountToPayZip,
        bidderPaysProcessingFee,
        currency,
        formatMessage,
        paymentMethod,
        processingFeeDifference,
        requireACHForCheckout,
    ]);

    const onClickCard = useCallback(() => {
        onSelect(paymentMethod);
    }, [onSelect, paymentMethod]);

    return (
        <div
            className={cn(
                'mb-md flex h-[80px] w-1/3 max-w-[230px] cursor-pointer flex-col items-center justify-center border border-border-secondary p-sm [&_*]:cursor-pointer',
                {
                    'border-border-selected': isSelected,
                    'border-4': isSelected,
                    'hover:border-icon-info': !isSelected,
                    'hover:border-4': !isSelected,
                }
            )}
            data-testid={`payment-method-selection-card-${paymentMethod}`}
            onClick={onClickCard}
        >
            <div>{title}</div>
            {Boolean(subtitle) && <div className="mt-2xs">{subtitle}</div>}
        </div>
    );
};
